import { ESCAPE } from '@angular/cdk/keycodes';
import { OverlayRef } from '@angular/cdk/overlay';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ArkikDialogContainerComponent } from './arkik-dialog.component';

export class ArkikDialogRef<T, R = any> {
  private _beforeClose = new Subject<R>();
  private _afterClosed = new Subject<R>();
  private _closeTimeOut: any;
  private _resultData?: R;
  /** Gets an observable that is notified when the dialog start closing */
  beforeClose = this._beforeClose.asObservable();
  /** Gets an observable that is notified when the dialog is finished closing. */
  afterClosed = this._afterClosed.asObservable();
  /** Whether the user is allowed to close the dialog. */
  private enableClose?: boolean = this._containerInstance._config.enableClose;

  /** The instance of component opened into the dialog. */
  componentInstance!: T;

  public get isDialogClosed(): boolean {
    return this._afterClosed.isStopped;
  }

  constructor(
    private _overlayRef: OverlayRef,
    private _containerInstance: ArkikDialogContainerComponent,
  ) {
    _overlayRef
      .keydownEvents()
      .pipe(
        filter((k) => k.keyCode === ESCAPE && this.enableClose),
        take(1),
      )
      .subscribe((_) => this.close());

    _overlayRef
      .backdropClick()
      .pipe(
        filter((_) => this.enableClose),
        take(1),
      )
      .subscribe((_) => this.close());

    _containerInstance._animationStateChanged
      .pipe(
        filter((e) => e.state === 'closed'),
        take(1),
      )
      .subscribe(() => {
        clearTimeout(this._closeTimeOut);
        this._finishDialogClose();
      });

    _containerInstance._dialogClose.pipe(take(1)).subscribe(() => this.close());

    _overlayRef.detachments().subscribe(() => {
      this._beforeClose.next(this._resultData);
      this._beforeClose.complete();
      this._afterClosed.next(this._resultData);
      this._afterClosed.complete();
      this.componentInstance = null;
      this._overlayRef.dispose();
    });
  }

  /**
   * Close the dialog
   * @param data Optional data to return to the dialog opener
   */
  close(data: R = null): void {
    this._resultData = data;

    this._containerInstance._animationStateChanged
      .pipe(
        filter((e) => e.state === 'closing'),
        take(1),
      )
      .subscribe(() => {
        this._beforeClose.next(data);
        this._beforeClose.complete();
        this._overlayRef.detachBackdrop();

        this._closeTimeOut = setTimeout(() => this._finishDialogClose(), 0 + 100);
      });

    this._containerInstance._startExitAnimation();
  }

  private _finishDialogClose(): void {
    this._overlayRef.dispose();
  }
}
